import { useTranslation } from "react-i18next";
import TermDepositContainer from "./TermDepositContainer";
import { useEffect, useMemo, useState } from "react";
import { IForm, IStep } from "./type.d";
import { useForm, yupResolver } from "@mantine/form";
import * as yup from "yup";
import { useDisclosure } from "@mantine/hooks";
import { IconArrowLeft, IconCheck } from "@tabler/icons-react";
import {
  ActionIcon,
  Box,
  Button,
  createStyles,
  Flex,
  MantineTheme,
  Modal,
  Stepper,
  Text,
} from "@mantine/core";
import notProcessed from "assets/images/pngs/notProcessed.png";
import { useLocation, useNavigate } from "react-router-dom";
import { TypeSavings } from "@/dto/savings/Savings.d";
import {
  useConfirmCreateSavingAccountMutation,
  useCreateSavingAccountMutation,
} from "@/services/savingsAPI";
import { RESULT_CODE } from "@/constants/enum";
import useSavingStore from "@/stores/SavingStore/savingStore";
import { useGenerateOTPMutation } from "@/services/authAPI";
import { SHA256 } from "crypto-js";
import OtpModal from "@/components/common/modal/otp-modal/OtpModal";
import { notifications } from "@mantine/notifications";
import { useGetMoneySourceQuery } from "@/services/bankAPI";
import { getDefaultTransferSource } from "@/helpers/moneySource";
import { get } from "lodash";
import { useCheckSourceAccountResQuery } from "@/services/dashboardAPI";
import RoutesString from "@/pages/routesString";

const useStylesStepper = createStyles((theme: MantineTheme) => ({
  stepIcon: {
    "&[data-progress]": {
      borderWidth: 0,
      backgroundColor: theme.colors.primary[7],
      color: "#fff",
    },
    "&[data-completed]": {
      borderWidth: 0,
      backgroundColor: theme.colors.green[7],
      color: "#fff",
    },
  },
}));


const TermDepositWrapper = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const schema = useMemo(
    () =>
      yup.object().shape({
        savingAmount: yup
          .number()
          .typeError(`${t("DEPOSIT_AMOUNT")} ${t("FIELD_REQUIRED")}`)
          .required(`${t("DEPOSIT_AMOUNT")} ${t("FIELD_REQUIRED")}`)
          .min(100000, t("MIN_DEPOSIT_AMOUNT") as string),
        savingsClassType: yup
          .string()
          .required(`${t("INTEREST_COLLECTION_METHOD")} ${t("FIELD_REQUIRED")}`),
        savingsDurationType: yup
          .string()
          .required(`${t("TERM_TYPE")} ${t("FIELD_REQUIRED")}`),
        rollOverType: yup
          .string()
          .required(`${t("FORMS_OF_PRINCIPAL_AND_INTEREST_RENEWAL")} ${t("FIELD_REQUIRED")}`),
      }),
    [t],
  );
  const { classes: classesStepper } = useStylesStepper();
  const [step, setStep] = useState<IStep>(1);
  const { state } = useLocation();
  const [otpId, setOtpId] = useState<string>("");
  const [
    OpenedTransactionNotProcessed,
    { open: OpenTransactionNotProcessed, close: CloseTransactionNotProcessed },
  ] = useDisclosure(false);
  const [opened, { open: OpenOTPCode, close: CloseOTPCode }] =
    useDisclosure(false);
  const [createSavings, { isLoading: isLoadingStepOne }] =
    useCreateSavingAccountMutation();
  const [generateOTP, { isLoading: isLoadingStepTwo }] =
    useGenerateOTPMutation();
  const [confirmSaving] = useConfirmCreateSavingAccountMutation();
  const [stateSavingStore, actionSavingStore] = useSavingStore();

  const { data: moneySourceData } = useGetMoneySourceQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const sources = useMemo(() => {
    return moneySourceData?.body?.sources || [];
  }, [moneySourceData]);
  const [sourceLinkId, setSourceLinkId] = useState<number>(
    getDefaultTransferSource(sources)?.id ?? 0,
  );

  const { data: sourceDataResponse } = useCheckSourceAccountResQuery(
    {
      sourceLinkId: sourceLinkId.toString(),
    },
    {
      skip: !sourceLinkId,
    },
  );

  const availableBalance = get(sourceDataResponse, "body.availableBalance", "");

  useEffect(() => {
    if (availableBalance) {
      form.setFieldValue("sourceAvailable", availableBalance);
    }
  }, [availableBalance]);

  useEffect(() => {
    const defaultSource = getDefaultTransferSource(sources);
    if (defaultSource) {
      setSourceLinkId(defaultSource.id);
    }
  }, [sources]);

  const handleChangeSourceLink = async (request: number) => {
    if (!request) {
      form.setErrors({
        sourceLinkId: "Chua chon ngan hang",
      });
      return;
    }
    setSourceLinkId(request);
  };

  const form = useForm<IForm>({
    initialValues: {
      sourceAccount: "", // nguồn tiền
      savingsType: "", // loại tiết kiêm
      savingsDuration: "", // kỳ hạn gửi
      savingsDurationType: "", // loại kỳ hạn
      durationUnit: "", // đơn vị kỳ hạn
      savingsClassType: "", // hình thức lĩnh lãi
      interestPaymentCycle: "", // chu kỳ lĩnh lãi
      rollOverType: "", // loại tái tục
      currency: "VND", // đơn vị tiền tệ
      savingAmount: null,
      calculatedInterestRate: null,
      method: "MANUAL",
      otp: "",
    },
    validate: yupResolver(schema),
  });

  console.log("state?.type", state?.type);
  const handleSubmitStepOne = async () => {
    const accountNumber = sources.find(
      (source) => source.id === sourceLinkId,
    )?.sourceNumber;
    form.setFieldValue("sourceAccount", accountNumber || "");
    const payload = {
      sourceAccount: accountNumber || "",
      savingAmount: Number(form.values.savingAmount),
      savingsType: state?.type ?? "",
      savingsDuration: Number(form.values.savingsDuration),
      durationUnit: form.values.durationUnit ?? "MONTH",
      savingsClassType: form.values.savingsClassType ?? "",
      rollOverType: form.values.rollOverType ?? "",
      currency: "VND",
    };
    if (form.values.interestPaymentCycle) {
      Object.assign(payload, {
        interestPaymentCycle: form.values.interestPaymentCycle,
      });
    }
    if (state?.type === TypeSavings.RECURRING_DEPOSIT && !form.values.method) {
      form.setFieldError("method", t("MUST_NOT_BE_EMPTY"));
      return;
    }
    if (form.values.method) {
      const periodic = {
        method: form.values.method,
      };
      if (form.values.method === "AUTO") {
        const methodAuto = {
          frequency: form.values.frequency,
          amount: form.values.amount,
        };
        Object.assign(periodic, {
          frequency: form.values.frequency,
          amount: form.values.amount,
        });
      }
      Object.assign(payload, { periodicDeposit: periodic });
    }
    const validation = form.validate();
    if (validation.hasErrors) {
      return;
    }
    if (
      form.values.savingAmount &&
      Number(form.values.savingAmount) > 500000000
    ) {
      OpenTransactionNotProcessed();
      return;
    }
    try {
      const res = await createSavings(payload).unwrap();
      if (res?.body.resultCode === RESULT_CODE.SUCCESS) {
        actionSavingStore.setSavingsId(res.body.savingsId);
        actionSavingStore.setSavingDetail(res.body.detail);
        actionSavingStore.setDetailParams(res.body.detailParams || []);
        setStep(2);
      } else {
        notifications.show({
          title: t("ERROR"),
          message: res.body?.resultDesc || t("ERROR"),
          color: "red",
        });
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmitStepTwo = async () => {
    const otpResponse = await generateOTP({
      otpType: "SMS_OTP",
      objectId: "1234",
      objectType: "tranfer",
      phoneNumber: "0987655679",
    }).unwrap();
    if (otpResponse.body?.resultCode === "0") {
      const resultOTP = otpResponse.body?.otpId;
      setOtpId(resultOTP);
      OpenOTPCode();
    }
  };

  const getTitle = () => {
    switch (state?.type) {
      case TypeSavings.REGULAR_TERM_DEPOSIT:
        return t("OPEN_REGULAR_TERM_DEPOSIT_TITLE");
      case TypeSavings.FLEXIBLE_TERM_DEPOSIT:
        return t("OPEN_FLEXIBLE_TERM_DEPOSIT_TITLE");
      default:
        return t("OPEN_ACCUMULATION_DEPOSIT_TITLE");
    }
  };

  const handleConfirm = async () => {
    const res = await confirmSaving({
      otpId,
      otp: SHA256(form.values.otp || "").toString(),
      transactionId: stateSavingStore.savingsId,
    }).unwrap();
    if (res?.body.resultCode === RESULT_CODE.SUCCESS) {
      CloseOTPCode();
      setStep(3);
    } else {
      notifications.show({
        title: t("ERROR"),
        message: res.body?.resultDesc || t("ERROR"),
        color: "red",
      });
    }
    // CloseOTPCode();
  };

  useEffect(() => {
    if (opened) {
      form.setFieldValue("otp", "");
    }
  }, [opened]);
  return (
    <>
      <Box>
        {/* #55978/#55984: man ket qua (buoc 3) theo thiet ke khong co tieu de,
            nut quay lai va thanh buoc — truoc day render vo dieu kien nen bi thua. */}
        {step !== 3 && (
          <Flex align="center" justify="space-between" gap="sm">
            <Flex align="center" gap="sm">
              <ActionIcon
                variant="transparent"
                onClick={() => navigate("/term-deposit/list-savings")}
                aria-label="Back to accounts"
                sx={{ cursor: "pointer" }}
              >
                <IconArrowLeft color="#000" />
              </ActionIcon>
              <Text fw={600} size="20px">
                {getTitle()}
              </Text>
            </Flex>
            <Stepper
              completedIcon={<IconCheck />}
              w="30%"
              active={step - 1}
              breakpoint="md"
              classNames={classesStepper}
              allowNextStepsSelect={false}
            >
              <Stepper.Step icon="1" label={t("DEPOSIT_ACCOUNT_OPENING_INFO")} />
              <Stepper.Step icon="2" label={t("CONFIRM")} />
            </Stepper>
          </Flex>
        )}
        <TermDepositContainer
          step={step}
          form={form}
          onSubmitStepOne={handleSubmitStepOne}
          onSubmitStepTwo={handleSubmitStepTwo}
          isLoadingStepOne={isLoadingStepOne}
          isLoadingStepTwo={isLoadingStepTwo}
          onBackStepOne={() => {
            form.reset();
            setStep(1);
          }}
          sourceLinkId={sourceLinkId}
          sources={sources}
          onChangeSourceLink={handleChangeSourceLink}
          onSubmitStepThree={() => {
            form.reset();
            actionSavingStore.clearStore();
            navigate(RoutesString.TransactionList);
          }}
        />
      </Box>
      {/* <OTpCode
        opened={opened}
        close={CloseOTPCode}
        setShowSuccessModal={handleConfirm}
      /> */}
      <OtpModal
        opened={opened}
        onClose={CloseOTPCode}
        onConfirm={handleConfirm}
        expireTime={60}
        otp={form.values.otp}
        onChangeOtp={(value) => form.setFieldValue("otp", value)}
      />
      <Modal
        opened={OpenedTransactionNotProcessed}
        onClose={CloseTransactionNotProcessed}
        centered
        withCloseButton={false}
        size="369px"
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "10px",
            textAlign: "center",
          }}
        >
          <img width="133px" height="133px" src={notProcessed} alt="" />
          <Text color="#3D3D3D" weight={700} size="18px">
            {t("TRANSACTION_NOT_PROCESSED")}
          </Text>
          <Text px="xl" my="sm">
            {t("AMOUNT_MAX_500M_NOTE")}
          </Text>

          <Button
            variant="filled"
            sx={{
              backgroundColor: "#2A81D0",
              borderRadius: "8px",
              "&:hover": {
                backgroundColor: "#1E679E",
              },
            }}
            fullWidth
            onClick={CloseTransactionNotProcessed}
          >
            {t("CLOSE")}
          </Button>
        </Box>
      </Modal>
    </>
  );
};

export default TermDepositWrapper;
